import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Instagram, Facebook, ArrowRight } from 'lucide-react';
import Logo from './Logo';
import { COMPANY_INFO } from '../mock';
import { BRANDS } from '../data/brands';
import { toast } from '../hooks/use-toast';

const Footer = () => {
  const [email, setEmail] = useState('');

  const submitNewsletter = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Enter a valid email', description: 'We need an email address to send drop alerts to.' });
      return;
    }
    const list = JSON.parse(localStorage.getItem('fad_newsletter') || '[]');
    if (!list.includes(email)) list.push(email);
    localStorage.setItem('fad_newsletter', JSON.stringify(list));
    toast({ title: 'You\u2019re on the list', description: `We\u2019ll email ${email} when new parts and wheel drops go live.` });
    setEmail('');
  };

  return (
    <footer className="bg-black text-white border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-10 lg:gap-12">
        {/* Brand + Newsletter */}
        <div>
          <Logo />
          <p className="text-neutral-400 text-sm mt-5 max-w-md leading-relaxed">
            Premium carbon fiber, forged wheels, and bespoke parts for the world&rsquo;s most iconic
            performance cars. Made-to-order in Canada, shipped worldwide.
          </p>
          <div className="mt-7">
            <div className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold mb-3">DROP ALERTS</div>
            <form onSubmit={submitNewsletter} className="flex border border-white/30 hover:border-white/60 transition-colors max-w-md" data-testid="footer-newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="footer-newsletter-input"
                className="flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="bg-white text-black hover:bg-[#FF6B00] hover:text-white transition-colors px-4 font-display tracking-[0.22em] text-xs font-bold flex items-center gap-2"
              >
                <ArrowRight size={14} />
              </button>
            </form>
          </div>
        </div>

        {/* Shop */}
        <div>
          <h4 className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] mb-4 font-semibold">SHOP</h4>
          <ul className="space-y-3 text-sm text-neutral-300">
            <li><Link className="hover:text-white transition-colors" to="/wheels">Forged Wheels</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/body-kits">Body Kits</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/shop/bmw">BMW</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/shop/porsche">Porsche</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/shop/tesla">Tesla</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/shop">All Brands</Link></li>
          </ul>
        </div>

        {/* Carbon Brands */}
        <div>
          <h4 className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] mb-4 font-semibold">CARBON BRANDS</h4>
          <ul className="space-y-3 text-sm text-neutral-300">
            {BRANDS.map((b) => (
              <li key={b.slug}>
                <Link className="hover:text-white transition-colors" to={`/brands/${b.slug}`}>{b.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] mb-4 font-semibold">COMPANY</h4>
          <ul className="space-y-3 text-sm text-neutral-300">
            <li><Link className="hover:text-white transition-colors" to="/about">About</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/process">Our Process</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/services">Services</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/faq">FAQ</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/contact">Contact</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/terms">Terms &amp; Privacy</Link></li>
          </ul>
        </div>

        {/* Connect */}
        <div>
          <h4 className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] mb-4 font-semibold">CONNECT</h4>
          <ul className="space-y-3 text-sm text-neutral-300">
            <li className="flex items-start gap-2">
              <Mail size={14} className="mt-1 text-neutral-500 flex-shrink-0" />
              <a className="hover:text-white transition-colors break-all" href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
            </li>
            {COMPANY_INFO.address && (
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 text-neutral-500 flex-shrink-0" />
                <span>{COMPANY_INFO.address}</span>
              </li>
            )}
          </ul>

          <div className="flex items-center gap-3 mt-6">
            <a
              href={COMPANY_INFO.socials.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="h-9 w-9 flex items-center justify-center border border-white/20 text-neutral-300 hover:text-white hover:border-[#FF6B00] transition-colors"
            >
              <Instagram size={16} />
            </a>
            <a
              href={COMPANY_INFO.socials.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              className="h-9 w-9 flex items-center justify-center border border-white/20 text-neutral-300 hover:text-white hover:border-[#FF6B00] transition-colors"
            >
              <Facebook size={16} />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-neutral-500">
          <span>&copy; {new Date().getFullYear()} Fortune Auto Design Inc. All rights reserved.</span>
          <span>Premium carbon fiber &middot; Forged wheels &middot; Made-to-order</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
